// ============================================================
// useAsks - one seat asking another, and waiting for the answer.
//
// One listener on `asks`. From it:
//
//   incoming  the pending asks addressed to THIS seat, oldest first
//   outgoing  the pending asks this seat is waiting on
//
// and the verbs. `ask` writes one and resolves when it is answered (with
// the answer) or cancelled (with null); the asker then deletes the
// document, so the collection holds only what is still open. Every move
// by the other end is a compare-and-set on the stored ask (`engine/asks`),
// so two people answering at once cannot both win.
//
// Outside a session there is nobody to ask: `ask` resolves null at once,
// and callers that can answer themselves (the GM, a solo player) never
// call it.
// ============================================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { answerAsk, cancelAsk, isAddressedTo, isPending, openAsk, readAsk, seeAsk } from '../engine/asks';
import type { Ask, AskAddressee } from '../engine/asks';

interface AsksSource {
  onAsksUpdate(
    sessionId: string,
    callback: (read: { entries: { id: string; data: Record<string, unknown> }[] }) => void,
  ): () => void;
  putAsk(sessionId: string, ask: Ask): Promise<void>;
  transitionAsk(sessionId: string, id: string, transition: (stored: unknown) => unknown | null): Promise<boolean>;
  deleteAsk(sessionId: string, id: string): Promise<void>;
}

interface Params {
  sessionId: string | null | undefined;
  mp: AsksSource;
  /** This seat: the account, whether it is the GM's, and the name shown beside what it asks. */
  seat: { uid: string | null; isGm: boolean; name: string };
}

export interface Asks {
  /** Every ask this build can read, settled ones too (the GM answers a Pinned question for a player from these). */
  all: Ask[];
  incoming: Ask[];
  outgoing: Ask[];
  /** Ask, and wait. Resolves with the answer, or null when cancelled or when there is nobody to ask. */
  ask<A>(kind: string, to: AskAddressee, payload: unknown): Promise<A | null>;
  answer(id: string, value: unknown): void;
  /** Withdraw one of mine, or dismiss one addressed to me. */
  cancel(id: string): void;
  markSeen(id: string): void;
  /**
   * Asks of mine that ENDED with nobody on this screen waiting (2026-10-06): the asker reloaded
   * while they were out, and the promise died with the page. Whoever can still use the answer
   * reads it here and then `claim`s it, which deletes it.
   */
  unclaimed: Ask[];
  claim(id: string): void;
}

/** The asks in a collection read that this build can understand, oldest first. */
function readAsks(entries: { id: string; data: Record<string, unknown> }[]): Ask[] {
  return entries
    .map((entry) => {
      try {
        return readAsk(entry.id, JSON.parse(entry.data.data as string));
      } catch {
        return null; // an unparseable document is not an ask
      }
    })
    .filter((ask): ask is Ask => ask !== null)
    .sort((a, b) => a.createdAt - b.createdAt);
}

/** A transition over a stored document: read it as an ask, apply, or leave it alone. */
const stored =
  (id: string, step: (ask: Ask) => Ask | null) =>
  (raw: unknown): Ask | null => {
    const ask = readAsk(id, raw);
    return ask ? step(ask) : null;
  };

type Waiter = (answer: unknown) => void;

/** What the listener keeps between reads about this seat's own asks. */
interface Waiting {
  /** The promise of each ask of mine that has not ended. */
  waiters: Map<string, Waiter>;
  /** Written but not yet seen in a read: absent because it has not arrived, not because it was removed. */
  unconfirmed: Set<string>;
  /** Ended on this screen (a wait resolved, or claimed) and being deleted: not news while a read still lists it. */
  ended: Set<string>;
}

/** Is this ask of mine still waiting? An absent one is, until a read has confirmed my write arrived. */
function stillWaiting(ask: Ask | undefined, id: string, unconfirmed: Set<string>): boolean {
  if (!ask) return unconfirmed.has(id);
  unconfirmed.delete(id);
  return isPending(ask);
}

/**
 * End the waits a read has settled: answered (with the answer), cancelled,
 * or removed under me (both null). What was read is deleted, so the
 * collection holds only what is still open.
 */
function settle(waiting: Waiting, asks: Ask[], forget: (id: string) => void) {
  const { waiters, unconfirmed, ended } = waiting;
  const byId = new Map(asks.map((a) => [a.id, a]));
  forgetDeleted(ended, byId);
  for (const [id, resolve] of [...waiters]) {
    const ask = byId.get(id);
    if (stillWaiting(ask, id, unconfirmed)) continue;
    waiters.delete(id);
    resolve(ask?.status === 'answered' ? ask.answer : null);
    if (ask) endHere(ended, id, forget);
  }
}

/** What a read no longer lists has been deleted: nothing left to remember about it. */
function forgetDeleted(ended: Set<string>, byId: Map<string, Ask>): void {
  for (const id of [...ended]) if (!byId.has(id)) ended.delete(id);
}

/** An ask this screen is done with: remembered as ended until its delete lands, and deleted. */
function endHere(ended: Set<string>, id: string, forget: (id: string) => void): void {
  ended.add(id);
  forget(id);
}

/** The asks on the wire, and this seat's waits settled as they arrive. */
function useAskList(sessionId: Params['sessionId'], mp: AsksSource, waiting: Waiting) {
  const [asks, setAsks] = useState<Ask[]>([]);
  useEffect(() => {
    if (!sessionId) return undefined;
    let live = true;
    const forget = (id: string) => void mp.deleteAsk(sessionId, id).catch(console.error);
    const unsub = mp.onAsksUpdate(sessionId, (read) => {
      if (!live) return;
      const next = readAsks(read.entries);
      settle(waiting, next, forget);
      setAsks(next);
    });
    return () => {
      live = false;
      unsub();
    };
  }, [sessionId, mp, waiting]);
  return asks;
}

export default function useAsks({ sessionId, mp, seat }: Params): Asks {
  const waiting = useRef<Waiting>({ waiters: new Map(), unconfirmed: new Set(), ended: new Set() }).current;
  const asks = useAskList(sessionId, mp, waiting);

  // A count beside the clock: several asks made in one millisecond (a test called for a group)
  // must not share an id, or each overwrites the last and its asker waits for ever.
  const minted = useRef(0);
  const ask = useCallback(
    <A>(kind: string, to: AskAddressee, payload: unknown): Promise<A | null> => {
      if (!sessionId || !seat.uid) return Promise.resolve(null);
      const id = `ask-${Date.now()}-${(minted.current++).toString(36)}-${seat.uid.slice(0, 6)}`;
      const fresh = openAsk({ id, kind, from: { uid: seat.uid, name: seat.name }, to, payload, createdAt: Date.now() });
      waiting.unconfirmed.add(id);
      return new Promise<A | null>((resolve) => {
        waiting.waiters.set(id, resolve as Waiter);
        mp.putAsk(sessionId, fresh).catch((err) => {
          console.error(err);
          waiting.waiters.delete(id);
          waiting.unconfirmed.delete(id);
          resolve(null);
        });
      });
    },
    [sessionId, mp, seat.uid, seat.name, waiting],
  );

  const move = useCallback(
    (id: string, step: (a: Ask) => Ask | null) => {
      if (sessionId) mp.transitionAsk(sessionId, id, stored(id, step)).catch(console.error);
    },
    [sessionId, mp],
  );
  const answer = useCallback((id: string, value: unknown) => move(id, (a) => answerAsk(a, value)), [move]);
  const cancel = useCallback((id: string) => move(id, cancelAsk), [move]);
  const markSeen = useCallback((id: string) => move(id, seeAsk), [move]);

  const { uid, isGm } = seat;
  const incoming = useMemo(
    () => asks.filter((a) => isPending(a) && isAddressedTo(a, { uid, isGm })),
    [asks, uid, isGm],
  );
  const outgoing = useMemo(() => asks.filter((a) => isPending(a) && a.from.uid === uid), [asks, uid]);
  const unclaimed = useMemo(
    () =>
      asks.filter((a) => a.from.uid === uid && !isPending(a) && !waiting.waiters.has(a.id) && !waiting.ended.has(a.id)),
    [asks, uid, waiting],
  );
  const claim = useCallback(
    (id: string) => {
      waiting.ended.add(id);
      if (sessionId) mp.deleteAsk(sessionId, id).catch(console.error);
    },
    [sessionId, mp, waiting],
  );
  return { all: asks, incoming, outgoing, unclaimed, ask, answer, cancel, markSeen, claim };
}
